import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import get from 'lodash.get';
import { Grid, Paginator } from '@kis-technologies/kis-ui';
import CustomLookupLogo from 'assets/custom_lookup.png';
import MembershipScanlogo from 'assets/barcode_scan.png';
import { Footer } from 'components/Footer';
import { CardContainer } from 'components/CardContainer';
import { TileLayout } from 'components/TileLayout';
import { useActions } from 'hooks/useActions';
import {
  Configuration,
  selectors as configSelectors,
  WillCallCustomSearch,
  SearchOption
} from 'services/configuration';
import { actions as willCallActions } from 'services/willcall';
import { useStyles } from './styled';
import { ArrowPagination } from 'components/Pagination';

const getOrderedOptions = (options: SearchOption[], tileOrder?: string[]): SearchOption[] => {
  let orderedOptions: SearchOption[] = [];
  const enabledOptions = [...options];
  if (tileOrder && tileOrder.length) {
    tileOrder.forEach((tile) => {
      const tileOption = enabledOptions.find((item) => item.name === tile);
      if (tileOption) {
        orderedOptions = orderedOptions.concat(enabledOptions.splice(enabledOptions.indexOf(tileOption), 1));
      }
    });
  }
  orderedOptions = orderedOptions.concat(enabledOptions);

  return orderedOptions;
};

const WillCallSelectionView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { from?: string };

  const setSelectedLookupOption = useActions(willCallActions.setSelectedLookupOption);
  const configuration = useSelector(configSelectors.selectConfiguration) as Configuration;

  const scanMembershipCard = get(configuration, 'willCall.scanMembershipCard');
  const customSearch: WillCallCustomSearch = get(configuration, 'willCall.customSearch', {});
  let enabledCustomOptions: SearchOption[] = [];
  if (customSearch.enabled && customSearch.tileOptions) {
    enabledCustomOptions = customSearch.tileOptions.filter((op) => op.enabled);
  }
  if (scanMembershipCard && scanMembershipCard.enabled) {
    enabledCustomOptions.push({ name: 'scanMembershipCard' });
  }

  const willCallTileOrder: string[] = get(configuration, 'willCall.willCallTileOrder', []);
  const orderedTiles = getOrderedOptions(enabledCustomOptions, willCallTileOrder);
  const touchForward = get(configuration, 'configuration.globalSettings.touchForward.general.enabled');
  const messages = get(configuration, 'willCall.messages');
  const pageHeader = get(messages, `header.${i18n.language}`, t('willCallSelection.searchTickets'));

  const [willcallOptions, setWillCallOptions] = useState<SearchOption | null>(null);
  const [selectedCard, setSelectedCard] = useState<null | number>(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const followingPages = ['/willcall/lookup', '/willcall/orders-list'];
    if (state && state.from && followingPages.includes(state.from)) {
      setSelectedLookupOption(null);
      if (!(orderedTiles && orderedTiles.length > 1)) {
        onPrevPageClick();
      }
    } else {
      if (orderedTiles && orderedTiles.length === 1) {
        setSelectedLookupOption(orderedTiles[0]);
        navigate('/willcall/lookup');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (willcallOptions && touchForward) {
      nextButtonClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [willcallOptions]);

  const onPrevPageClick = () => {
    // re-routes to the previous page (StartPurchaseView)
    navigate('/ticketing/start-purchase', { state: { from: location.pathname } });
  };

  const nextButtonClick = () => {
    setSelectedLookupOption(willcallOptions);
    navigate('/willcall/lookup');
  };

  const onCardClick = (option: SearchOption, idx: number) => {
    setWillCallOptions(option);
    setSelectedCard(idx);
  };

  const classes = useStyles();

  return (
    <>
      {orderedTiles && orderedTiles.length && (
        <>
          <TileLayout headerText={pageHeader}>
            <Paginator
              paginationComponent={ArrowPagination}
              items={orderedTiles}
              itemsPerPage={3}
              page={currentPage}
              onPageChange={(newPage) => setCurrentPage(newPage)}
            >
              {({ items }) => (
                <Grid container className={classes.selectionCardContainer}>
                  {items.map((option, idx) => {
                    const optionToShow = option && option.name;
                    switch (optionToShow) {
                      case 'scanMembershipCard':
                        return (
                          <CardContainer
                            imgSrc={MembershipScanlogo}
                            cardAction={() => onCardClick(option, idx)}
                            descText={
                              get(scanMembershipCard, `label.${i18n.language}`) ||
                              t('willCallSelection.scanCardDescription')
                            }
                            key={idx}
                            isSelected={selectedCard === idx}
                          />
                        );
                      default:
                        return (
                          <CardContainer
                            imgSrc={CustomLookupLogo}
                            cardAction={() => onCardClick(option, idx)}
                            descText={get(option, `label.${i18n.language}`, '')}
                            key={idx}
                            isSelected={selectedCard === idx}
                          />
                        );
                    }
                  })}
                </Grid>
              )}
            </Paginator>
          </TileLayout>
          {/* Footer */}
          <Footer
            onPrevClick={onPrevPageClick}
            nextText={touchForward ? undefined : t('commonText.next')}
            onNextClick={nextButtonClick}
            isNextDisabled={selectedCard === null}
          />
        </>
      )}
    </>
  );
};

export default WillCallSelectionView;
